<template>
    <div
        class="bundle-template-line"
    >
        <BundleTemplatesLineItem
            v-for="(option, index) in line.optionsData"
            :key="`bundle-templates-line-item-${option.attributeUid}`"
            :model-value="models[index]!"
            :option="option"
            :options-disabled-model="(!index && optionsCount > 1) ? void(0) : optionsDisabledModel[option.attributeUid]"
            class="d-flex flex-column gap-4"
            @update:model-value="onChange(option.attributeUid, $event, index)"
        ></BundleTemplatesLineItem>
        <BundleTemplatesLineItemStatus
            v-if="currentSku"
            :current-sku="currentSku"
            :key="currentSku.skuUid"
        ></BundleTemplatesLineItemStatus>
    </div>
</template>

<script lang="ts" setup>
    import BundleTemplatesLineItem from '~/components/products/product/bundle-templates/BundleTemplatesLineItem/BundleTemplatesLineItem.vue';
    import BundleTemplatesLineItemStatus from '~/components/products/product/bundle-templates/BundleTemplatesLineItemStatus/BundleTemplatesLineItemStatus.vue';

    import type {
        BundleTemplateProcessedModel,
        BundleTemplateProcessedModelItem,
        BundleTemplateLineSkuData,
        ProductOrderOptionAttrs,
        ProductAttrsDisabledModel,
        GenericObject,
        BundleTemplateLine,
        ProductAttrsMaps
    } from '~/types';
    import {
        useProductOptions,
        useBundleTemplates,
        useDebug
    } from '~/composables';
    import { isUndefined as lo_isUndefined } from 'es-toolkit';
    import { xor as lo_xor } from 'es-toolkit';

    const props = defineProps<{
        modelValue: Nilish<GenericObject<Nullable<string>>[]>;
        line: BundleTemplateLine;
        skusMap: GenericObject<string[]>;
        attrsMap: ProductAttrsMaps;
        lineUid: string;
        isEnabled?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: [models: GenericObject<string>[], processed: BundleTemplateProcessedModelItem[]]];
    }>();

    const {
        checkOptions,
        getProcessedModelItem,
        getOrderedAttrsFromModel,
        isProcessedModelsValid
    } = useBundleTemplates();
    const {
        getDisabledOptionsModel,
        findMissingAttrValues,
        getAllCombosFromArrays
    } = useProductOptions();

    const debugConsole = useDebug();
    const getProcessedModelItemFn = getProcessedModelItem.bind(0, props.line);
    const missingOptions = ref<string[][]>(
        props.line.lineType === 'sku' ? getMissingOptionValues() : []
    );

    debugConsole.info(`Missing Options: (${props.line.uid}): `, missingOptions);

    const models: GenericObject<string>[] = reactive(getModelFromAttrVals(props.modelValue));
    const attrsMap = computed<GenericObject<string[]>>(() => {
        const { attrs, attrsVals } = props.attrsMap;
        return models.flatMap((o) => Object.values(o)).reduce((acc: GenericObject<string[]>, attrVal) => {
            const key = attrsVals[attrVal];
            if (key) {
                const obj = attrs[key];
                if (obj) {
                    acc[key] = obj;
                }
            }
            return acc;
        }, {});
    });

    const currentSku = ref<Nilish<BundleTemplateLineSkuData>>(null);
    const optionsModelOrderedAttrsVals = computed<ProductOrderOptionAttrs>(
        () => getOrderedAttrsFromModel(models)
    );
    const optionsDisabledModel = ref<ProductAttrsDisabledModel>(
        getDisabledOptionsModel(Object.assign({}, ...models))
    );

    const optionsCount = computed<number>(() => props.line.optionsData.length);

    function checkOptionsFn(): void {
        checkOptions(
            attrsMap.value,
            optionsModelOrderedAttrsVals,
            missingOptions,
            toggleDisabledModel
        );
    }

    checkOptionsFn();

    function getModelFromAttrVals(lookupModels: Nilish<GenericObject<Nullable<string>>[]>) {
        return props.line.optionsData.map((o) => {
            const { attributeUid: uid } = o;
            const preSetItem = lookupModels?.find((m) => m[uid]);
            const value = preSetItem?.[uid] || o.defaultValueUid || o.values[0]?.uid || '';
            return { [ uid ]: value };
        });
    }

    function getMissingOptionValues(): string[][] {
        if (props.skusMap) {
            const attrsLookup = props.line.optionsData.map((o) => o.values.map((o) => o.uid));
            const combosMap = getAllCombosFromArrays<string>(...attrsLookup);
            return findMissingAttrValues(props.skusMap, combosMap);
        }

        return [];
    }

    function onChange(attrUid: string, uid: string, index: number): void {
        models[index]![attrUid] = uid;
        checkOptionsFn();
        processModelItem(attrUid, uid, index);
    }

    function toggleDisabledModel(disable: boolean, option: string, value: string): void {
        const method = disable ? 'add' : 'delete';
        optionsDisabledModel.value[option]![method](value);
    }

    function findNextBestSku(attrUid: string, uid: string, index: number): void {
        const values = models.flatMap((o) => Object.values(o));

        let bestSku = null;
        let bestVal = values;
        const colorIdx = props.line.optionsData.findIndex((o) => o.inputType === 'color');

        if (colorIdx > -1) {
            const colorAttr = values[colorIdx];
            const lookup = uid !== colorAttr ? [uid] : values.splice(colorIdx, 1);

            for (const [k, newVals] of Object.entries(props.skusMap)) {
                const isCompat = lookup.every((s) => newVals.includes(s));
                if (isCompat) {
                    bestVal = newVals;
                    bestSku = k;
                    break;
                }
            }
        }

        if (bestSku) {
            const vals = props.skusMap[bestSku] ?? [];
            const { attrsVals } = props.attrsMap;
            const newModel = vals.reduce((acc: GenericObject<string>[], attrVal) => {
                const key = attrsVals[attrVal];
                if (key) {
                    acc.push({ [key]: attrVal });
                }
                return acc;
            }, []);

            Object.assign(models, newModel);
            checkOptionsFn();
        }
    }

    function processModelItem(attrUid?: string, uid?: string, index?: number): void {
        let { sku, processed } = getProcessedModelItemFn(models);
        const isValid = isProcessedModelsValid(processed, props.line);

        if (
            !isValid &&
            !lo_isUndefined(attrUid) &&
            !lo_isUndefined(uid) &&
            !lo_isUndefined(index)
        ) {
            findNextBestSku(attrUid, uid, index);
            ({ sku, processed } = getProcessedModelItemFn(models));
        }

        currentSku.value = sku;
        emit('update:modelValue', [models, processed]);
    }

    const unWatchIsEnabled = watch(
        () => props.isEnabled,
        (newVal) => {
            if (newVal) {
                processModelItem();
            }
        }
    );

    onBeforeMount(() => {
        processModelItem();
    });

    onBeforeUnmount(() => {
        unWatchIsEnabled();
    });
</script>

<style lang="scss">
.bundle-template-line-item-button:has(+ .bundle-template-line-item-button) {
    &:after {
        content: '';
        display: block;
        width: 96%;
        margin: auto;
        height: 1px;
        background-color: rgba(var(--v-border-color), var(--v-border-opacity));
    }
}
</style>
